import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { foodApi } from "../../api/client";
import client from "../../api/client";
import BarcodeScanner from "./BarcodeScanner";

// ─── Quantity units we let users define for a custom food ─────────────────────
const QUANTITY_UNITS = [
  { key: "piece",      label: "Piece",       placeholder: "g per piece"      },
  { key: "cup",        label: "Cup",         placeholder: "g per cup"        },
  { key: "bowl",       label: "Bowl",        placeholder: "g per bowl"       },
  { key: "glass",      label: "Glass",       placeholder: "g per glass"      },
  { key: "tablespoon", label: "Tablespoon",  placeholder: "g per tbsp"       },
  { key: "teaspoon",   label: "Teaspoon",    placeholder: "g per tsp"        },
  { key: "slice",      label: "Slice",       placeholder: "g per slice"      },
  { key: "plate",      label: "Plate",       placeholder: "g per plate"      },
];

export default function CustomFoodModal({ onClose, onSaved }) {
  const { appUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showQuantities, setShowQuantities] = useState(false);

  // ── Barcode state ──────────────────────────────────────────────────────────
  const [showBarcode, setShowBarcode] = useState(false);
  const [barcodeLooking, setBarcodeLooking] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null); // holds full nutrition data from OFF
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [scannerMode, setScannerMode] = useState("choose"); // tracks BarcodeScanner internal mode

  // ── Nutrition state (per 100g) ─────────────────────────────────────────────
  const [formData, setFormData] = useState({
    dish_name: "",
    calories_kcal: "",
    protein_g: "",
    carbohydrates_g: "",
    sugars_g: "",
    dietary_fiber_g: "",
    fat_total_g: "",
  });

  // ── Quantity state ─────────────────────────────────────────────────────────
  const [quantities, setQuantities] = useState(
    QUANTITY_UNITS.reduce((acc, q) => ({ ...acc, [q.key]: "" }), {})
  );

  // ── Custom units ───────────────────────────────────────────────────────────
  const [customUnits, setCustomUnits] = useState([]);

  const addCustomUnit = () => {
    setCustomUnits((prev) => [...prev, { name: "", grams: "" }]);
  };

  const updateCustomUnit = (index, field, value) => {
    setCustomUnits((prev) =>
      prev.map((u, i) => (i === index ? { ...u, [field]: value } : u))
    );
  };

  const removeCustomUnit = (index) => {
    setCustomUnits((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuantityChange = (key, value) => {
    setQuantities((q) => ({ ...q, [key]: value }));
  };

  // ── Barcode detected — look it up ─────────────────────────────────────────
  const handleBarcodeDetected = async (barcode) => {
    setBarcodeLooking(true);
    setError("");
    setScannedBarcode(barcode);

    try {
      const { data } = await client.get(`/api/food/barcode/${barcode}`);

      if (data.success && data.food) {
        const food = data.food;
        setBarcodeData(food);

        // Prefill the form with the fetched data
        setFormData({
          dish_name: food.dish_name || "",
          calories_kcal: food.calories_kcal != null ? String(food.calories_kcal) : "",
          protein_g: food.macros?.protein_g != null ? String(food.macros.protein_g) : "",
          carbohydrates_g: food.macros?.carbohydrates_g != null ? String(food.macros.carbohydrates_g) : "",
          sugars_g: food.macros?.sugars_g != null ? String(food.macros.sugars_g) : "",
          dietary_fiber_g: food.macros?.dietary_fiber_g != null ? String(food.macros.dietary_fiber_g) : "",
          fat_total_g: food.macros?.fat_total_g != null ? String(food.macros.fat_total_g) : "",
        });

        // Hide barcode scanner and show the form
        setShowBarcode(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || "Product not found. You can still fill in the details manually.";
      setError(msg);
      setShowBarcode(false);
      setBarcodeData(null);
    } finally {
      setBarcodeLooking(false);
    }
  };

  // ── Build quantities payload ───────────────────────────────────────────────
  const buildQuantities = () => {
    const out = {};
    let any = false;
    for (const { key } of QUANTITY_UNITS) {
      const raw = quantities[key];
      const grams = parseFloat(raw);
      if (Number.isFinite(grams) && grams > 0) {
        out[key] = +(100 / grams).toFixed(4);
        any = true;
      }
    }
    for (const cu of customUnits) {
      const name = cu.name.trim().toLowerCase();
      const grams = parseFloat(cu.grams);
      if (name && Number.isFinite(grams) && grams > 0) {
        out[name] = +(100 / grams).toFixed(4);
        any = true;
      }
    }
    return any ? out : null;
  };

  // ── Submit — save to database ──────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const builtQuantities = buildQuantities();

    // Build the full schema payload. For barcode-sourced foods we include
    // the detailed vitamin/mineral data from Open Food Facts. For manual
    // entries they stay null.
    const newFoodRecord = {
      dish_name: formData.dish_name,
      calories_kcal: parseFloat(formData.calories_kcal) || 0,
      macros: {
        protein_g: parseFloat(formData.protein_g) || 0,
        carbohydrates_g: parseFloat(formData.carbohydrates_g) || 0,
        sugars_g: parseFloat(formData.sugars_g) || 0,
        dietary_fiber_g: parseFloat(formData.dietary_fiber_g) || 0,
        fat_total_g: parseFloat(formData.fat_total_g) || 0,
        water_g: null,
      },
      fats_breakdown: barcodeData?.fats_breakdown || {
        saturated_g: null,
        monounsaturated_g: null,
        polyunsaturated_g: null,
        cholesterol_mg: null,
      },
      vitamins: barcodeData?.vitamins || {
        a_mcg: null, b1_mg: null, b2_mg: null, b3_mg: null, b5_mg: null,
        b6_mg: null, b11_mcg: null, b12_mcg: null, c_mg: null, d_mcg: null,
        e_mg: null, k_mcg: null,
      },
      minerals: barcodeData?.minerals || {
        sodium_mg: null, calcium_mg: null, iron_mg: null, magnesium_mg: null,
        manganese_mg: null, phosphorus_mg: null, potassium_mg: null,
        selenium_mcg: null, zinc_mg: null, copper_mg: null,
      },
      ...(builtQuantities ? { quantities: builtQuantities } : {}),
      createdBy: appUser?.email || "unknown_user",
      // If this food came from a barcode scan, flag it
      ...(scannedBarcode ? { viaBarcode: true } : {}),
    };

    try {
      const { data } = await foodApi.createCustom(newFoodRecord);
      onSaved(data.food);
    } catch (err) {
      setError(err.message || "Failed to save food.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md bg-[#12121a] border border-white/10 rounded-2xl p-6 shadow-2xl overflow-y-auto max-h-[90vh]"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Add Custom Food</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl">&times;</button>
        </div>

        {!barcodeLooking && (
        <p className="text-sm text-slate-400 mb-4">
          {showBarcode ? "Scan a barcode or enter it manually" : "Can't find it? Add it yourself! Nutrition values should be per 100g."}
        </p>
        )}

        {/* ── Barcode option ─────────────────────────────────────────────── */}
        {!barcodeLooking && (
        <AnimatePresence mode="wait">
          {showBarcode ? (
            <motion.div
              key="barcode-scanner"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mb-4"
            >
              <BarcodeScanner
                onBarcodeDetected={handleBarcodeDetected}
                onClose={() => setShowBarcode(false)}
                onModeChange={setScannerMode}
                loading={barcodeLooking}
              />
              {scannerMode === "choose" && (
              <button
                type="button"
                onClick={() => setShowBarcode(false)}
                className="w-full mt-3 text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                Cancel barcode scan
              </button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="barcode-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-4"
            >
              <button
                type="button"
                onClick={() => setShowBarcode(true)}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 hover:border-emerald-500/40 transition-all group"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/30 transition-colors">
                  <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M3 9V6a3 3 0 013-3h3M21 9V6a3 3 0 00-3-3h-3M3 15v3a3 3 0 003 3h3M21 15v3a3 3 0 01-3 3h-3M7 12h10" />
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <p className="text-emerald-300 font-semibold text-sm">Add via Barcode</p>
                  <p className="text-slate-500 text-xs">Scan or enter barcode to auto-fill nutrition</p>
                </div>
                <svg className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        )}

        {/* ── Barcode lookup loading state ─────────────────────────────────── */}
        {barcodeLooking && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 mb-4 pt-8 pb-5 px-5 bg-indigo-500/5 border border-indigo-500/20 rounded-xl flex flex-col items-center gap-3 overflow-visible"
          >
            {/* Animated scanner pulse */}
            <div className="relative w-16 h-16 overflow-visible">
              <div className="absolute -inset-2 rounded-xl border-2 border-indigo-500/30 animate-ping" />
              <div className="absolute inset-1 rounded-lg border-2 border-indigo-400/50 animate-pulse" />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-7 h-7 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3 9V6a3 3 0 013-3h3M21 9V6a3 3 0 00-3-3h-3M3 15v3a3 3 0 003 3h3M21 15v3a3 3 0 01-3 3h-3M7 12h10" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <p className="text-indigo-300 text-sm font-semibold">Barcode scanned ✓</p>
              <p className="text-slate-500 text-xs mt-1">Fetching nutrition details...</p>
            </div>
            {/* Animated dots */}
            <div className="flex gap-1.5">
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </motion.div>
        )}

        {/* ── Prefilled indicator ─────────────────────────────────────────── */}
        {!showBarcode && scannedBarcode && barcodeData && (
          <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-start gap-3">
            {barcodeData.image_url && (
              <img
                src={barcodeData.image_url}
                alt=""
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-white/5"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-emerald-300 text-sm font-medium">
                ✓ Prefilled from barcode
              </p>
              <p className="text-slate-500 text-xs mt-0.5 truncate">
                {barcodeData.brand ? `${barcodeData.brand} · ` : ""}Code: {scannedBarcode}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setScannedBarcode(null);
                setBarcodeData(null);
                setFormData({ dish_name: "", calories_kcal: "", protein_g: "", carbohydrates_g: "", sugars_g: "", dietary_fiber_g: "", fat_total_g: "" });
              }}
              className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"
              title="Clear barcode data"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* ── Form (hidden when barcode scanner is active) ────────────────── */}
        {!showBarcode && !barcodeLooking && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Dish Name *</label>
            <input
              required type="text" name="dish_name"
              value={formData.dish_name} onChange={handleChange}
              style={{ fontSize: "16px" }}
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none"
              placeholder="e.g., Mom's Chicken Curry"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Calories (kcal) *</label>
              <input required type="number" step="0.1" name="calories_kcal"
                value={formData.calories_kcal} onChange={handleChange}
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Protein (g) *</label>
              <input required type="number" step="0.1" name="protein_g"
                value={formData.protein_g} onChange={handleChange}
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Carbs (g) *</label>
              <input required type="number" step="0.1" name="carbohydrates_g"
                value={formData.carbohydrates_g} onChange={handleChange}
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Total Fat (g) *</label>
              <input required type="number" step="0.1" name="fat_total_g"
                value={formData.fat_total_g} onChange={handleChange}
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Sugars (g)</label>
              <input type="number" step="0.1" name="sugars_g"
                value={formData.sugars_g} onChange={handleChange}
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">Fiber (g)</label>
              <input type="number" step="0.1" name="dietary_fiber_g"
                value={formData.dietary_fiber_g} onChange={handleChange}
                style={{ fontSize: "16px" }}
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none" placeholder="0" />
            </div>
          </div>

          {/* ── Quantities (optional) ──────────────────────────────────────── */}
          <div className="pt-1 border-t border-white/5">
            <button
              type="button"
              onClick={() => setShowQuantities((s) => !s)}
              className="w-full flex items-center justify-between text-left py-3"
            >
              <div>
                <p className="text-sm font-semibold text-white">Serving sizes <span className="text-slate-500 font-normal">(optional)</span></p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Define units like "1 piece", "1 cup" so you can log this without weighing it.
                </p>
              </div>
              <svg
                className={`w-4 h-4 text-slate-500 transition-transform ${showQuantities ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showQuantities && (
              <div className="space-y-3 pb-1">
                <div className="grid grid-cols-2 gap-3">
                  {QUANTITY_UNITS.map((u) => (
                    <div key={u.key}>
                      <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">
                        1 {u.label}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          inputMode="decimal"
                          value={quantities[u.key]}
                          onChange={(e) => handleQuantityChange(u.key, e.target.value)}
                          placeholder={u.placeholder}
                          style={{ fontSize: "16px" }}
                          className="w-full px-3 py-2.5 pr-8 rounded-xl bg-black/40 border border-white/10 text-white focus:border-indigo-500 outline-none text-sm"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">g</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Custom units */}
                {customUnits.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-white/5">
                    <p className="text-xs text-indigo-400 font-medium">Custom Units</p>
                    {customUnits.map((cu, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={cu.name}
                          onChange={(e) => updateCustomUnit(i, "name", e.target.value)}
                          placeholder="Unit name (e.g. clove)"
                          style={{ fontSize: "16px" }}
                          className="flex-1 px-3 py-2.5 rounded-xl bg-black/40 border border-indigo-500/30 text-white placeholder:text-slate-600 focus:border-indigo-500 outline-none text-sm"
                        />
                        <div className="relative w-24">
                          <input
                            type="number"
                            step="0.1"
                            min="0.1"
                            inputMode="decimal"
                            value={cu.grams}
                            onChange={(e) => updateCustomUnit(i, "grams", e.target.value)}
                            placeholder="grams"
                            style={{ fontSize: "16px" }}
                            className="w-full px-3 py-2.5 pr-7 rounded-xl bg-black/40 border border-indigo-500/30 text-white placeholder:text-slate-600 focus:border-indigo-500 outline-none text-sm"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">g</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCustomUnit(i)}
                          className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                          aria-label="Remove custom unit"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add custom unit button */}
                <button
                  type="button"
                  onClick={addCustomUnit}
                  className="w-full py-2.5 rounded-xl border border-dashed border-indigo-500/30 text-indigo-400 text-sm font-medium hover:bg-indigo-500/5 hover:border-indigo-500/50 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add custom unit
                </button>
              </div>
            )}
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl font-semibold text-slate-400 bg-white/5 hover:bg-white/10 transition">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-3 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition disabled:opacity-50">
              {loading ? "Saving..." : "Save to Database"}
            </button>
          </div>
        </form>
        )}
      </motion.div>
    </div>
  );
}
